import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, LogIn } from "lucide-react";
import AdminCustomerDetail from "../components/AdminCustomerDetail";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx, usd } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function AdminCustomers() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [createForm, setCreateForm] = useState(null);

  const can = (p) => ["admin", "super_admin"].includes(user?.role) || (user?.permissions || []).includes(p);

  const load = useCallback((s = "") => api.get("/admin/customers", { params: s ? { search: s } : {} }).then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => { const { data } = await api.get(`/admin/customers/${id}`); setDetail(data); };

  const doImpersonate = async (id) => {
    if (!window.confirm("Enter this customer's account? Your actions will be recorded in the audit log.")) return;
    try { await impersonate(id); navigate("/dashboard"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const refresh = () => { load(search); if (detail) openDetail(detail.user.id); };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/customers/${c.id}`); toast.success("Customer deleted"); setDetail(null); load(search);
  };
  const createCustomer = async () => {
    try {
      await api.post("/admin/customers", createForm);
      toast.success("Customer created"); setCreateForm(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fx-s3" />
          <Input data-testid="cust-search" value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value); }} placeholder="Search name or email" className="pl-9 h-9 bg-fx-s1 border-white/10" />
        </div>
        {can("users.create") && <Button onClick={() => setCreateForm({ name: "", email: "", password: "" })} data-testid="cust-new-btn" className="bg-brandblue hover:bg-blue-500 gap-2 ml-auto"><Plus className="h-4 w-4" /> New Customer</Button>}
      </div>
      <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-fx-s3 border-b border-white/5">
            <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Live Balance</th><th className="px-4 py-3">KYC</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-fx-s3">No customers found.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} data-testid={`cust-row-${c.id}`} className="border-b border-white/[0.03]">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-fx-s3">{c.email}</td>
                <td className="px-4 py-3 font-mono">{usd(c.wallets?.live || 0)}</td>
                <td className="px-4 py-3 text-xs text-fx-s3">{c.kyc_status?.replace("_", " ")}</td>
                <td className="px-4 py-3"><span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", c.status === "suspended" ? "text-neg bg-neg/15" : "text-pos bg-pos/15")}>{c.status || "active"}</span></td>
                <td className="px-4 py-3 text-xs text-fx-s3">{timeAgo(c.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button onClick={() => openDetail(c.id)} data-testid={`cust-view-${c.id}`} size="sm" className="bg-brandblue hover:bg-blue-500 h-7 mr-2">View</Button>
                  {can("users.impersonate") && <Button onClick={() => doImpersonate(c.id)} data-testid={`cust-impersonate-${c.id}`} size="sm" variant="outline" className="border-white/10 bg-fx-base h-7 gap-1"><LogIn className="h-3.5 w-3.5" /> Login as</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminCustomerDetail detail={detail} can={can} onClose={() => setDetail(null)} onRefresh={refresh} onImpersonate={doImpersonate} onDelete={del} />

      {/* Create dialog */}
      <Dialog open={!!createForm} onOpenChange={(o) => !o && setCreateForm(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="cust-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Create Customer</DialogTitle></DialogHeader>
          {createForm && (
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid="cust-create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Email</Label><Input data-testid="cust-create-email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="bg-fx-base border-white/10" /></div>
              <div className="space-y-1"><Label className="text-xs">Temporary Password</Label><Input data-testid="cust-create-password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="bg-fx-base border-white/10" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={createCustomer} data-testid="cust-create-save" className="bg-brandblue hover:bg-blue-500">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
